/* eslint-disable import/no-restricted-paths */
import { useContext, useMemo, useState, useEffect } from "react";
import {
  Typography,
  tokens,
  UsageScenarioSelector,
  UtilityConnectionStatusPill,
  DEFAULT_GUIDANCE_TEXT,
  UsageInfoGuidance,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { type ContractReviewMessage } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { EnergyUsageForm } from "components/organisms/EnergyUsageForm";
import { Card, CardHeaderDivider } from "components/atoms/Card";
import { UsageCadence } from "utils/usageUtils";
import { UsageFormContext } from "providers/UsageFormContext";
import { UsageType } from "constants/usageTypes";
import { SplatProspectResponse } from "providers/Types";
import { calculateUsageData } from "utils/usageUtils";
import { ContractReviewBanners } from "components/molecules/ContractReviewBanners";
import { is12MonthActualUsageMethodMessage } from "utils/contractReviewUtils";

const USAGE_OFFSET_CALCULATOR_LINK =
  "https://current.sunrun.com/site/f84222bb-aaed-5912-ab79-cb9dd8c4743c/page/3b59890c-6461-455e-983d-5f79ea96518d";

type UsageInfoProps = {
  additionalSystem?: boolean;
  isSunrunManaged?: boolean;
  monthlyUsageData: (number | undefined)[] | null;
  setMonthlyUsageData: (arr: (number | undefined)[]) => void;
  usageCadence: UsageCadence;
  setUsageCadence: (cadence: UsageCadence) => void;
  usageType: UsageType;
  setUsageType: (type: UsageType) => void;
  homeSize?: number;
  setHomeSize?: (size: number) => void;
  isReadOnly?: boolean;
  usage: SplatProspectResponse | undefined;
  getBillOnline?: () => void;
  intervalDataDeclined?: boolean;
  intervalDataDeclinedReason?: string;
  fetchingIntervalDataStatus?: boolean;
  setIntervalDataDeclinedReason?: (reason: string) => void;
  intervalDataUsed?: boolean;
  intervalDataInitialLoading?: boolean;
  intervalDataStatus?: string | null;
  contractReviewMessages?: ContractReviewMessage[];
};

const UsageInfo = ({
  additionalSystem,
  isSunrunManaged,
  monthlyUsageData,
  setMonthlyUsageData,
  usageCadence,
  setUsageCadence,
  usageType,
  setUsageType,
  homeSize,
  setHomeSize,
  isReadOnly = false,
  usage,
  getBillOnline,
  intervalDataDeclined,
  intervalDataDeclinedReason,
  setIntervalDataDeclinedReason,
  intervalDataUsed,
  intervalDataInitialLoading,
  intervalDataStatus,
  contractReviewMessages,
}: UsageInfoProps) => {
  const { setIsDirty } = useContext(UsageFormContext);
  const { monthsEntered } = useMemo(
    () => calculateUsageData({ prospect: usage }),
    [usage]
  );

  const [showScenarioSelector, setShowScenarioSelector] = useState(true);

  useEffect(() => {
    if (monthsEntered === 0) {
      setUsageType("full");
    } else if (monthsEntered === 12) {
      setUsageType("full");
      setShowScenarioSelector(false);
    } else {
      setUsageType("partial");
      setShowScenarioSelector(false);
    }
  }, [monthsEntered, setUsageType, usage]);

  const currentGuidanceText = useMemo(() => {
    if (additionalSystem && isSunrunManaged && usageType === "full") {
      return {
        ...DEFAULT_GUIDANCE_TEXT[usageType],
        title: "Helpful info guidance",
        bullets: [
          ...DEFAULT_GUIDANCE_TEXT[usageType].bullets,
          <>
            You may able to use the{" "}
            <Link href={USAGE_OFFSET_CALCULATOR_LINK} target="_blank">
              combined offset calculator
            </Link>
            , see this job aid
          </>,
        ],
      };
    }

    return DEFAULT_GUIDANCE_TEXT[usageType];
  }, [usageType, additionalSystem, isSunrunManaged]);

  const handleSelectScenario = (type: "full" | "partial" | "squareFootage") => {
    setUsageType(type);
    setShowScenarioSelector(false);
  };

  return (
    <Card>
      <HeaderContainer className="usage-info-header">
        <Typography size={tokens.FONT_SIZE_3}>Usage Info</Typography>
        <UtilityConnectionStatusPill status={intervalDataStatus || ""} />
      </HeaderContainer>
      <CardHeaderDivider />
      <ContractReviewBanners
        messages={contractReviewMessages ?? []}
        category="estimation_method_check"
        filter={is12MonthActualUsageMethodMessage}
      />
      {showScenarioSelector && !isReadOnly ? (
        <UsageScenarioSelector
          onSelectScenario={handleSelectScenario}
          getBillOnline={getBillOnline}
          utilityStatusLoading={intervalDataInitialLoading}
          utilityStatus={intervalDataStatus || ""}
        />
      ) : (
        <>
          <div className="usage-info-body">
            <StyledFlexContainer>
              <EnergyUsageForm
                usageData={monthlyUsageData}
                setUsageData={(data) => {
                  setMonthlyUsageData(data);
                  setIsDirty(true);
                }}
                guidance={
                  <UsageInfoGuidance
                    title={currentGuidanceText.title}
                    description={currentGuidanceText.description}
                    bullets={currentGuidanceText.bullets}
                  />
                }
                usageType={usageType}
                usageCadence={usageCadence}
                setUsageCadence={setUsageCadence}
                setUsageType={setUsageType}
                homeSize={homeSize}
                setHomeSize={setHomeSize}
                isReadOnly={isReadOnly}
                usage={usage}
                setIntervalDataDeclinedReason={setIntervalDataDeclinedReason}
                intervalDataDeclinedReason={intervalDataDeclinedReason}
              />
            </StyledFlexContainer>
          </div>
        </>
      )}
    </Card>
  );
};

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledFlexContainer = styled.div`
  display: flex;
  margin-top: 32px;
  @media (max-width: 900px) {
    display: block;
    section {
      margin-bottom: 20px;
    }
  }
`;

const Link = styled.a`
  color: inherit;
`;

export { UsageInfo };
